import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import AxisHelper from '../chart_helpers/AxisHelper'
import SizeHelper from '../chart_helpers/SizeHelper'
import { categoricalExtent } from '../chart_helpers/AxisHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'
import { tweenDentedRect } from '../utils/pathUtils'
import { interpolate as d3_interpolate, interpolateRgb as d3_interpolateRgb } from 'd3-interpolate'
import { isDarkMode } from '../utils/configUtils'
import { select as d3_select } from 'd3-selection'

const MAX_BAR_WIDTH = 99
export default class PercentageBarChart extends BaseChart {
  constructor(...args) {
    super(...args)
    this.zeroBased = true
  }
  static defaultConfig = {
    /**
     * {-EN-} Minimum height of each bar (in pixels).
     * {-ES-} Altura mínima de cada barra (en píxeles).
     */
    percentageMode: {
      type: [String],
      default: 'independent',
      options: ['aggregate', 'independent'],
      exposed: true,
    },
    dataSorting: {
      type: [String],
      default: 'none',
      options: ['none', 'asc', 'desc'],
      exposed: true,
    },
    minBarHeight: {
      type: [Number],
      default: 0,
      exposed: true,
    },
    horizontal: {
      type: Boolean,
      default: true,
      exposed: false,
      nonEditable: true,
    },
    showDatumValues: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    datumValuesPosition: {
      type: [String, null],
      default: null,
      options: ['center', 'insideBeginning', 'insideEnd'],
      exposed: true,
    },
    iconDict: {
      type: [Object, Array, null],
      default: null,
      exposed: true,
    },
    showIcons: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    hasColorPatterns: {
      type: Boolean,
      default: true,
      exposed: false,
    },
    AxisHelper: {
      zoom: {
        nonEditable: true,
      },
      scroll: {
        default: false,
        exposed: true,
      },
      showScrollbar: {
        default: true,
        exposed: true,
      },
      x: {
        scale: {
          value: 'band',
          exposed: false,
          nonEditable: true,
        },
        side: {
          value: 'top',
          exposed: false,
          nonEditable: true,
        },
        showTickValues: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        tickOrientation: {
          exposed: false,
          nonEditable: true,
        },
        tickWrap: {
          exposed: false,
          nonEditable: true,
        },
        valueFormat: {
          exposed: false,
          nonEditable: true,
        },
        grid: {
          exposed: false,
          nonEditable: true,
        },
        unit: {
          exposed: false,
          nonEditable: true,
        },
        trimThreshold: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        getExtent: function (data) {
          return categoricalExtent(data)
        },
      },
      y: {
        side: {
          value: 'right',
          exposed: false,
          nonEditable: true,
        },
        showTickValues: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        scale: {
          value: 'linear',
          exposed: false,
          nonEditable: true,
        },
        tickOrientation: {
          exposed: false,
          nonEditable: true,
        },
        tickWrap: {
          exposed: false,
          nonEditable: true,
        },
        trimThreshold: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        getExtent: function (data) {
          // FIJAMOS 0,100 o no!?!?!
          return [0, 100]
          // let extent, extentParts
          // extentParts = continuousExtentParts(data, d => [d.y0, d.y1])
          // // console.log("extent parts",extentParts)
          // extent = continuousExtent(extentParts)

          // // console.log("bar extent bef",extent)
          // extent = cleanCollisions(this.axes.y, extent, extentParts)
          // // console.log("bar extent aft",extent)
          // return extent
        },
      },
    },
    TooltipBehavior: {
      getItemOffset: function ($el) {
        let original = $el.getBoundingClientRect()
        return {
          x: original.x + original.width / 2,
          y: original.top, //- 5,
        }
      },
      getHtml: function (d) {
        if (this.data.length > 1)
          return `
          <div class="tip-value important">${this.formatTooltipValue(d.value, 'y')}</div>
          <div class="tip-id">
            <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
              { id: d.parentId }
            )}; margin-right: 3px"></span>
            ${d.parentId}
          </div>
          <div class="secondary">${this.formatValue(d.id, 'x')}</div>
            `
        else
          return `
        <div class="tip-value important">${this.formatTooltipValue(d.value, 'y')}</div>
        <div class="secondary">
            ${d.id}
          </div>
        `
      },
    },
  }
  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper, AxisHelper]
  checkConsistency() {
    return true
  }
  beforeHelpers() {
    this.margin = { top: 20, right: 100, left: 20, bottom: 20 }
  }
  draw() {
    let longDir, getTranslate, getOffset, getShort //,shortDir

    // let yScale = d3_scaleLinear().domain([0, 100]).range([0, this.contentSize.width])
    // let xScale = d3_scaleBand()
    //   .domain(categoricalExtent(this.data))
    //   .range([0, this.contentSize.height])

    longDir = 'height'
    // shortDir = 'width'
    getTranslate = (long, short) => {
      return `translate(${short},${long})`
    }

    let offsetValue
    if (this.axes.y.func.scale().domain()[0] < 0 && this.axes.y.func.scale().domain()[1] > 0)
      offsetValue = 0
    else offsetValue = this.axes.y.func.scale().domain()[0]

    getOffset = v => Math.min(v, this.axes.y.func.scale()(offsetValue))
    getShort = v => Math.abs(this.axes.y.func.scale()(offsetValue) - v)

    // let barPercent = 0.4
    // let barWidth = Math.min(MAX_BAR_WIDTH, this.axes.x.func.scale().bandwidth() * barPercent)
    const barBandWidth = this.axes.x.func.scale().bandwidth()
    let barWidth = Math.max(3, this.axes.x.func.scale().bandwidth() - (barBandWidth > 30 ? 30 : 18))
    let barMargin = (this.axes.x.func.scale().bandwidth() - barWidth) / 2
    let barLabelOfsset = barBandWidth > 30 ? 10 : 6

    function fillTween(d) {
      const fill = self.fill(d)
      if (fill?.includes('url'))
        return function (t) {
          return fill
        }
      else {
        const currentFill = d3_select(this).attr('fill')
        var i = d3_interpolateRgb(currentFill, fill)
        return function (t) {
          return i(t)
        }
      }
    }

    let drawItem
    let stackPadding = 2
    let self = this
    drawItem = (sel, status) => {
      sel.attr('transform', d => {
        let barPos
        if (status === 'enter' || status === 'exit') barPos = this.axes.y.func.scale()(0)
        else barPos = this.axes.y.func.scale()(this.config.horizontal ? d.y0 : d.y1)
        return getTranslate(
          this.axes.x.func.scale()(d.id) + barMargin,
          barPos + (this.config.horizontal ? 0 : stackPadding / 2)
        )
      })

      sel
        .select('.item-path')
        .attr('transform', `translate(0,${barLabelOfsset})`)
        .attr('fill', d => {
          if (d.parentId === '_total_')
            return isDarkMode(this.config, this._parent.accessibilityOptions)
              ? 'rgb(68, 68, 68)'
              : 'rgb(246, 250, 252)'
          else return this.color({ id: d.parentId })
        })
      // .attr('d', d => {
      if (sel.select('.item-path').attrTween) {
        sel.select('.item-path').attrTween('d', function (d) {
          let barLength
          if (status === 'enter' || status === 'exit') barLength = 0
          else
            barLength =
              getShort(self.axes.y.func.scale()(d.safeValue)) -
              (d.isFirstLayer ? stackPadding : stackPadding)
          barLength = Math.max(barLength, 0)
          // let w = longDir === 'width' ? barWidth : barLength
          // let h = longDir === 'width' ? barLength : barWidth

          //CUSTOMIZED:
          // let dents = [d.isLastLayer ? 'br' : null, d.isFirstLayer ? 'tl' : null].filter(d => d)
          // if (dents.length === 0) dents = 'none'
          // return dentedRect(w, h, dents)
          //WARNING AHORA LOS "dents" son IGUAL que en el barchart horizontal!!!!
          return tweenDentedRect.call(this, d, {
            barLength,
            barWidth,
            longDir,
            scale: self.axes.y.func.scale(),
          })
        })
        sel.select('.item-path').attrTween('fill', function (d) {
          return fillTween.bind(this)({ ...d, id: d.parentId })
        })
      }
    }

    let totalsData = this.data[0].value.map(d => {
      return {
        id: d.id,
        parentId: '_total_',
        y0: 0,
        y1: 100,
        value: 100,
        safeValue: 100,
        totalPercentage: d.layerTotal,
        totalAbs: d.layerTotalAbs,
      }
    })
    let totalsRoot = this.content.selectAll('.totals-layer').data([null])
    totalsRoot = totalsRoot.merge(totalsRoot.enter().append('g').attr('class', 'totals-layer'))
    totalsRoot.lower()
    let totalsItems = totalsRoot.selectAll('.total-item').data(totalsData, d => d.id)
    totalsItems.exit().remove()

    const ICON_HEIGHT = 23
    const ICON_WIDTH = 23
    let translate = [0, -ICON_HEIGHT / 1.3]

    let getIcon = id => {
      return this.config.showIcons ? this.config.iconDict?.[id]?.icon : null
    }

    let totalsItemsEnter = totalsItems.enter().append('g').attr('class', 'total-item')
    totalsItemsEnter.append('path').attr('class', 'item-path')
    totalsItemsEnter.append('text').attr('class', 'label')
    totalsItemsEnter.append('g').attr('class', 'value').append('text')
    totalsItems = totalsItems.merge(totalsItemsEnter)
    totalsItems.call(drawItem)
    totalsItems
      .select('.label')
      .style('font-size', '16px')
      // .style('font-family', 'santander-headline-bold-embedded')
      // .style('font-weight', 'bold')
      .attr('fill', isDarkMode(this.config, this._parent.accessibilityOptions) ? '#fff' : '#666')
      .attr('transform', d => `translate(${getIcon(d.id) ? ICON_WIDTH + 10 : 0}, 0)`)
      .text(
        d => d.id + (this.config.showDatumValues ? ` (${this.valueWithUnit(d.totalAbs, 'y')})` : '')
      )

    totalsItems
      .select('.value')
      .attr('transform', `translate(${this.size.width - 40},${10 + barWidth / 2})`)
      .select('text')
      // .text(d => parseFloat(d.totalPercentage).toFixed(2) + '%')
      .text(d => (this.formatValue(d.totalPercentage, 'y') + '%').replace('%%', '%'))
      .style('font-size', '18px')
      // .style('font-family', 'santander-headline-bold-embedded')
      .style('font-weight', '300')
      .attr('fill', isDarkMode(this.config, this._parent.accessibilityOptions) ? '#fff' : '#222')
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')

    this.itemsEnter.append('path').attr('class', 'item-path')
    // HAy que pintar lo "viejos" primero para assegurar que se mantiene el orden de colores...
    this.itemsTrans.call(drawItem)
    this.itemsEnter.call(drawItem, 'enter')
    this.itemsEnterTrans.call(drawItem)
    this.itemsExitTrans.style('opacity', 0)
    if (this.parentItemsExitTrans) this.parentItemsExitTrans.style('opacity', 0)

    //ICONS
    let iconGroup = totalsItems
      .merge(totalsItemsEnter)
      .selectAll('.icon')
      .data(d => {
        const icon = getIcon(d.id)
        return !!icon ? [{ id: d.id, ...icon }] : []
      })
    iconGroup.exit().remove()
    let iconGroupEnter = iconGroup.enter().append('g').attr('class', 'icon')
    //iconGroupEnter.style('opacity', 0).transition().duration(500).style('opacity', 1)
    iconGroup = iconGroup.merge(iconGroupEnter)
    iconGroup.attr('transform', `translate(${translate[0]}, ${translate[1]})`)
    iconGroup.html(d => (d.icon ? d.icon.svg : null))
    let iconGroupSvg = iconGroup.select('svg')
    //Hay q hardcodear nuevos ids en los defs y en los use para hacer unicas las modificaciones
    iconGroupSvg
      .select('defs')
      .select('path')
      .attr('id', function (d) {
        return this.getAttribute('id') ? this.getAttribute('id') + `annotation-icon-${d.id}` : null
      })
    iconGroupSvg.select('use').attr('xlink:href', function (d) {
      return this.getAttribute('xlink:href')
        ? this.getAttribute('xlink:href') + `annotation-icon-${d.id}`
        : null
    })
    iconGroupSvg.selectAll('path, polygon').attr('fill', function () {
      let d = d3_select(this.parentNode).datum()
      return d && d.color && !d.icon.disableColorModifier ? d.color : this.getAttribute('fill')
    })
    iconGroupSvg.attr('height', `${ICON_HEIGHT}px`).attr('width', `${ICON_WIDTH}px`)
    iconGroupSvg.select('use').attr('fill', function (d) {
      return d && d.color && !d.icon.disableColorModifier ? d.color : this.getAttribute('fill')
    })

    const getDatumValuesXPosition = d => {
      if (this.config.horizontal) {
        switch (this.config.datumValuesPosition) {
          case 'top':
            return getShort(this.axes.y.func.scale()(d.safeValue)) + 8
          case 'center':
            return getShort(this.axes.y.func.scale()(d.safeValue)) / 2
          case 'bottom':
            return -8
          case 'insideBeginning':
            return 8
          case 'insideEnd':
            return getShort(this.axes.y.func.scale()(d.safeValue)) - 8
        }
      } else {
        return barWidth / 2
      }
    }
    const getDatumValuesYPosition = d => {
      if (this.config.horizontal) {
        return 10 + barWidth / 2
      } else {
        switch (this.config.datumValuesPosition) {
          case 'top':
            return -8
          case 'insideEnd':
            return 12
          case 'center':
            return getShort(this.axes.y.func.scale()(d.safeValue)) / 2
          case 'bottom':
            return getShort(this.axes.y.func.scale()(d.safeValue)) + 16
          case 'insideBeginning':
            return getShort(this.axes.y.func.scale()(d.safeValue)) - 12
        }
      }
    }
    let datumValues = this.itemsEnter
      .merge(this.items)
      .selectAll('.datum-value')
      .data(d => {
        if (this.config.showDatumValues) {
          switch (this.config.datumValuesPosition) {
            case 'center':
            case 'insideBeginning':
            case 'insideEnd':
              return [{ value: d.value, safeValue: d.y1 - d.y0 }]
            case 'bottom':
              if (d.isFirstLayer) return [{ ...d, value: d.barMax + d.barMin }]
              return []
            case 'top':
              if (d.isLastLayer) return [{ ...d, value: d.barMax + d.barMin }]
              return []
            default:
              return []
          }
        } else {
          return []
        }
      })
    datumValues.exit().transition().style('opacity', 0).remove()
    datumValues = datumValues.merge(
      datumValues
        .enter()
        .append('text')
        .attr('class', 'datum-value')
        .style('opacity', 0)
        .style('font-size', '14px')
        .style('font-weight', 'bold')
        .attr('fill', 'currentColor')
        .attr('x', d => getDatumValuesXPosition(d))
        .attr('y', d => getDatumValuesYPosition(d))
        .text(d => this.formatValue(d.value, 'y'))
    )
    // datumValues
    //   .text(d => {
    //     return this.formatValue(d.value, 'y')
    //   })
    // .text(d => this.valueWithUnit(d.value, 'y'))
    // .transition()
    // .style('opacity', 1)

    datumValues
      .attr('transform', 'rotate(0)')
      .attr('text-anchor', this.config.horizontal ? 'start' : 'middle')
      .style('dominant-baseline', 'middle')
      .style('stroke', 'unset')
      .style('fill', 'currentColor')

    datumValues
      .text(d => {
        return this.formatValue(d.value, 'y')
      })
      // .text(d => this.valueWithUnit(d.value, 'y'))
      .transition()
      .duration(1000)
      .attr('x', d => getDatumValuesXPosition(d))
      .attr('y', d => getDatumValuesYPosition(d))
      .style('opacity', 1)
      .textTween(function (d) {
        let i = d3_interpolate(this._currentValue || 0, d.value)
        return t => {
          return self.formatValue((this._currentValue = i(t)), 'y')
        }
      })

    if (this.config.datumValuesPosition === 'bottom') {
      if (this.config.horizontal) {
        datumValues.attr('text-anchor', 'end')
      }
    }

    if (['center', 'insideBeginning', 'insideEnd'].includes(this.config.datumValuesPosition)) {
      /*
      if (!this.config.horizontal) {
        datumValues.attr(
          'transform',
          d => `rotate(-90, ${getDatumValuesXPosition(d)}, ${getDatumValuesYPosition(d)})`
        )
      }
      */
      datumValues.style('dominant-baseline', 'central')
      datumValues.style('paint-order', 'stroke')
      datumValues.style('stroke', 'white')
      datumValues.style('stroke-width', '2px')
      datumValues.style('stroke-linecap', 'butt')
      datumValues.style('stroke-linejoin', 'round')
      datumValues.style('fill', 'black')
    }

    if (this.config.datumValuesPosition === 'center') {
      datumValues.attr('text-anchor', 'middle')
    }

    if (this.config.datumValuesPosition === 'insideBeginning') {
      if (this.config.horizontal) {
        datumValues.attr('text-anchor', 'start')
      }
    }

    if (this.config.datumValuesPosition === 'insideEnd') {
      if (this.config.horizontal) {
        datumValues.attr('text-anchor', 'end')
      }
    }
  }
}
